import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { gql, useMutation, useQuery } from '@apollo/client';
import toast from 'react-hot-toast';
import { GET_NOTICES } from '../../graphql/queries';
import { CREATE_NOTICE, DELETE_NOTICE, UPDATE_NOTICE } from '../../graphql/mutations';
import { Spinner } from '../common/Spinner';
import NoticeFormModal, {
  NOTICE_TYPES,
  type AdminNotice,
  type NoticeFormValues,
} from './notice/NoticeFormModal';

const COLUMN_COUNT = 5;

interface GetNoticesResponse {
  getNotices: {
    notices: AdminNotice[];
    count: number;
  };
}

interface NoticeMutationResponse {
  success: boolean;
  message?: string | null;
  error?: string | null;
}

/** `getNotices(noticeType:)` filters server-side; '' means "all types". */
type TypeFilter = '' | (typeof NOTICE_TYPES)[number];

const TYPE_FILTERS: readonly TypeFilter[] = ['', ...NOTICE_TYPES];

/**
 * Admin-side 공지사항 management page at `/admin/notices`.
 *
 * Lists every notice newest-first and owns create / edit / delete. The write
 * mutations require `notices:manage`, which the route also gates on, so a
 * permission error here means the token is stale rather than that the UI let
 * an unauthorised admin through.
 *
 * `getNotices` returns the full set (the resolver Scans and sorts by
 * `createdAt` desc) with no cursor, so there is no pagination to thread —
 * unlike the sibling inquiry page.
 */
const AdminNoticeListPage: React.FC = () => {
  const { t } = useTranslation(['admin', 'common']);
  const navigate = useNavigate();

  const [typeFilter, setTypeFilter] = useState<TypeFilter>('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editing, setEditing] = useState<AdminNotice | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { data, loading, error, refetch } = useQuery<GetNoticesResponse>(
    gql`${GET_NOTICES}`,
    {
      variables: { noticeType: typeFilter || undefined },
      fetchPolicy: 'cache-and-network',
    },
  );

  const [createNotice, { loading: creating }] = useMutation(gql`${CREATE_NOTICE}`);
  const [updateNotice, { loading: updating }] = useMutation(gql`${UPDATE_NOTICE}`);
  const [deleteNotice] = useMutation(gql`${DELETE_NOTICE}`);

  const notices = data?.getNotices?.notices ?? [];
  const isSubmitting = creating || updating;

  const openCreate = () => {
    setEditing(null);
    setIsFormOpen(true);
  };

  const openEdit = (notice: AdminNotice) => {
    setEditing(notice);
    setIsFormOpen(true);
  };

  const closeForm = () => {
    if (isSubmitting) return;
    setIsFormOpen(false);
    setEditing(null);
  };

  /**
   * A mutation can fail two ways: a thrown GraphQL/network error, or a
   * resolved payload with `success: false` (validation, missing notice,
   * permission denial). Both are surfaced as a toast; only success closes the
   * dialog and refetches.
   */
  const handleSubmit = async (values: NoticeFormValues) => {
    const isEdit = editing !== null;
    try {
      const response = isEdit
        ? await updateNotice({
            variables: { input: { noticeId: editing.noticeId, ...values } },
          })
        : await createNotice({ variables: { input: values } });

      const result: NoticeMutationResponse | undefined = isEdit
        ? response.data?.updateNotice
        : response.data?.createNotice;

      if (!result?.success) {
        toast.error(
          result?.error ||
            (t(isEdit ? 'admin:notices.toast.updateFailed' : 'admin:notices.toast.createFailed') as string),
        );
        return;
      }

      toast.success(
        t(isEdit ? 'admin:notices.toast.updated' : 'admin:notices.toast.created') as string,
      );
      setIsFormOpen(false);
      setEditing(null);
      await refetch();
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.warn('[admin] notice save failed', err);
      toast.error(
        err?.message ||
          (t(isEdit ? 'admin:notices.toast.updateFailed' : 'admin:notices.toast.createFailed') as string),
      );
    }
  };

  const handleDelete = async (notice: AdminNotice) => {
    const confirmed = window.confirm(
      t('admin:notices.deleteConfirm', { title: notice.title }) as string,
    );
    if (!confirmed) return;

    setDeletingId(notice.noticeId);
    try {
      const response = await deleteNotice({ variables: { noticeId: notice.noticeId } });
      const result: NoticeMutationResponse | undefined = response.data?.deleteNotice;

      if (!result?.success) {
        toast.error(result?.error || (t('admin:notices.toast.deleteFailed') as string));
        return;
      }

      toast.success(t('admin:notices.toast.deleted') as string);
      if (expandedId === notice.noticeId) setExpandedId(null);
      await refetch();
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.warn('[admin] notice delete failed', err);
      toast.error(err?.message || (t('admin:notices.toast.deleteFailed') as string));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="min-h-full bg-gray-50">
      {/* Breadcrumb */}
      <div className="max-w-[1200px] mx-auto px-4 py-3">
        <nav className="text-sm text-gray-500">
          <button
            type="button"
            onClick={() => navigate('/admin')}
            className="hover:text-blue-600 transition-colors"
          >
            {t('common:profileDropdown.adminDashboard')}
          </button>
          <span className="mx-1.5">&gt;</span>
          <span>{t('admin:sidebar.customerService')}</span>
          <span className="mx-1.5">&gt;</span>
          <span className="text-gray-900 font-medium">{t('admin:sidebar.notices')}</span>
        </nav>
      </div>

      <div className="max-w-[1200px] mx-auto px-4 pb-16 space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-bold text-gray-900">{t('admin:notices.title')}</h1>
          <button
            type="button"
            onClick={openCreate}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
          >
            {t('admin:notices.createButton')}
          </button>
        </div>

        {/* Type filter */}
        <div className="flex flex-wrap items-center gap-2">
          {TYPE_FILTERS.map((type) => {
            const isActive = typeFilter === type;
            return (
              <button
                key={type || 'all'}
                type="button"
                onClick={() => {
                  setTypeFilter(type);
                  setExpandedId(null);
                }}
                className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                  isActive
                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                    : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                }`}
              >
                {type || (t('admin:notices.filterAll') as string)}
              </button>
            );
          })}
        </div>

        {/* Global error banner (network / permission). */}
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error.message || (t('admin:notices.error.loadFailed') as string)}
          </div>
        )}

        {/* Table */}
        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-4 py-3 text-left font-medium">
                  {t('admin:notices.columns.type')}
                </th>
                <th className="px-4 py-3 text-left font-medium">
                  {t('admin:notices.columns.title')}
                </th>
                <th className="px-4 py-3 text-left font-medium">
                  {t('admin:notices.columns.createdAt')}
                </th>
                <th className="px-4 py-3 text-left font-medium">
                  {t('admin:notices.columns.updatedAt')}
                </th>
                <th className="px-4 py-3 text-right font-medium">
                  {t('admin:notices.columns.actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && notices.length === 0 ? (
                <tr>
                  <td colSpan={COLUMN_COUNT} className="px-4 py-16 text-center">
                    <Spinner size="medium" className="mx-auto" />
                  </td>
                </tr>
              ) : notices.length === 0 ? (
                <tr>
                  <td colSpan={COLUMN_COUNT} className="px-4 py-12 text-center text-gray-400">
                    {t('admin:notices.empty')}
                  </td>
                </tr>
              ) : (
                notices.map((notice) => {
                  const isExpanded = expandedId === notice.noticeId;
                  return (
                    <React.Fragment key={notice.noticeId}>
                      <tr className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-3 align-top">
                          <span className="rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                            {notice.noticeType || NOTICE_TYPES[0]}
                          </span>
                        </td>
                        <td className="px-4 py-3 align-top">
                          <button
                            type="button"
                            onClick={() =>
                              setExpandedId(isExpanded ? null : notice.noticeId)
                            }
                            aria-expanded={isExpanded}
                            className="text-left font-medium text-gray-900 hover:text-blue-600"
                          >
                            {notice.title}
                          </button>
                        </td>
                        <td className="px-4 py-3 align-top text-gray-500">
                          {notice.createdAt?.slice(0, 10) ?? '-'}
                        </td>
                        <td className="px-4 py-3 align-top text-gray-500">
                          {notice.updatedAt?.slice(0, 10) ?? '-'}
                        </td>
                        <td className="px-4 py-3 align-top text-right whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => openEdit(notice)}
                            className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-100"
                          >
                            {t('admin:notices.actions.edit')}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(notice)}
                            disabled={deletingId === notice.noticeId}
                            className="ml-2 rounded-md border border-red-200 px-2.5 py-1 text-xs font-medium text-red-600 transition-colors hover:bg-red-50 disabled:opacity-40"
                          >
                            {deletingId === notice.noticeId
                              ? t('admin:notices.actions.deleting')
                              : t('admin:notices.actions.delete')}
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="border-t border-gray-100 bg-gray-50">
                          <td colSpan={COLUMN_COUNT} className="px-4 py-4">
                            <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
                              {notice.content}
                            </p>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isFormOpen && (
        <NoticeFormModal
          notice={editing}
          isSubmitting={isSubmitting}
          onSubmit={handleSubmit}
          onClose={closeForm}
        />
      )}
    </div>
  );
};

export default AdminNoticeListPage;
